/**
 * pogi-atc-feedback.js — instant "Adding… → Added ✓" feedback on the add-to-cart CTA.
 *
 * Fills the ~1s gap between tap/click and the cart drawer opening. PURELY VISUAL:
 * it never preventDefaults, stops propagation, disables, or otherwise touches the
 * add-to-cart submit — it only swaps the [data-pogi-atc-label] text + a state class.
 *
 * Works on every [data-pogi-atc] button (PDP buy boxes + sticky bar), desktop AND
 * mobile, because they all bubble Shopify's cart-lines update event
 * (product-form.js). Delegated on `document` so it also covers buttons that are
 * rendered or morphed in later.
 *
 * Cross-browser notes: ES5-only (var, function exprs, index for-loops — no NodeList
 * .forEach, no arrow fns). `click` fires on tap on all touch devices, so no separate
 * touch handling is needed (and avoids double-firing). Uses only widely-supported DOM
 * APIs: closest (guarded), classList, dataset, querySelectorAll, setAttribute.
 */
(function () {
  'use strict';

  var ADDING = 'Adding…'; // "Adding…"
  var ADDED = 'Added ✓';  // "Added ✓"
  var ADDED_MS = 1400; // how long "Added ✓" stays before reverting
  var FALLBACK_MS = 6000; // auto-revert if no cart result arrives (e.g. failed add)
  var EVENT_FALLBACK_MS = 60000; // exceeds the bounded mutation + drawer-render lifecycle
  var CART_LINES_UPDATE = 'shopify:cart:lines-update';
  var CART_ERROR = 'shopify:cart:error';

  // POGI 2026-07-23 (i18n wave): strings come from window.POGI_I18N (emitted by
  // theme.liquid via | t | json); the EN literals stay as fallbacks.
  function t(key, fallback) {
    var i18n = window.POGI_I18N;
    return (i18n && i18n[key]) || fallback;
  }

  function labelOf(btn) {
    return btn.querySelector('[data-pogi-atc-label]');
  }

  function clearFeedbackTimer(btn) {
    if (typeof btn.__pogiFeedbackTimer === 'number') {
      window.clearTimeout(btn.__pogiFeedbackTimer);
    }
    delete btn.__pogiFeedbackTimer;
  }

  function disconnectLabelObserver(btn) {
    if (btn.__pogiFeedbackLabelObserver) {
      btn.__pogiFeedbackLabelObserver.disconnect();
    }
    delete btn.__pogiFeedbackLabelObserver;
  }

  function preserveBusyLabel(btn, label, generation) {
    disconnectLabelObserver(btn);
    if (!label || typeof MutationObserver !== 'function') return;
    var observer = new MutationObserver(function () {
      if (
        btn.__pogiFeedbackGeneration !== generation
        || !btn.classList.contains('is-adding')
      ) return;
      var adding = t('adding', ADDING);
      if (label.textContent === adding) return;
      // Buy Box v2 refreshes authoritative quantity and availability as soon
      // as Shopify confirms the line. Remember that newest idle label for the
      // eventual reset, but do not paint it over the still-truthful busy state
      // while the drawer section is morphing.
      btn.setAttribute('data-pogi-orig', label.textContent);
      label.textContent = adding;
    });
    observer.observe(label, { childList: true, characterData: true, subtree: true });
    btn.__pogiFeedbackLabelObserver = observer;
  }

  function nextFeedbackGeneration(btn) {
    var current = typeof btn.__pogiFeedbackGeneration === 'number'
      ? btn.__pogiFeedbackGeneration
      : 0;
    btn.__pogiFeedbackGeneration = current + 1;
    return btn.__pogiFeedbackGeneration;
  }

  function ownerForButton(btn) {
    var formId = btn.getAttribute('form');
    var form = formId ? document.getElementById(formId) : btn.form;
    return form && typeof form.closest === 'function'
      ? form.closest('product-form-component')
      : null;
  }

  function ownerForNode(node) {
    if (!node || node.nodeType !== 1) return null;
    if (node.matches && node.matches('product-form-component')) return node;
    var direct = typeof node.closest === 'function'
      ? node.closest('product-form-component')
      : null;
    if (direct) return direct;
    var product = typeof node.closest === 'function'
      ? node.closest('product-information')
      : null;
    return product ? product.querySelector('product-form-component') : null;
  }

  function toAdding(btn, owner) {
    var l = labelOf(btn);
    // A shopper can add again while the previous "Added ✓" confirmation is
    // still visible. Cancel that prior operation's reset before arming this
    // one; otherwise its stale timer can erase the new "Adding…" state.
    clearFeedbackTimer(btn);
    var generation = nextFeedbackGeneration(btn);
    if (l && typeof btn.getAttribute('data-pogi-orig') !== 'string') {
      btn.setAttribute('data-pogi-orig', l.textContent);
    }
    btn.classList.remove('is-added');
    btn.classList.add('is-adding');
    btn.__pogiAddOwner = owner;
    btn.setAttribute('aria-busy', 'true');
    if (l) l.textContent = t('adding', ADDING);
    preserveBusyLabel(btn, l, generation);
    if (window.pogiAnnounce) window.pogiAnnounce(t('sr_adding', 'Adding to cart')); // SR cue (pogi-qol.js)
    return generation;
  }

  function toAdded(btn) {
    var l = labelOf(btn);
    clearFeedbackTimer(btn);
    disconnectLabelObserver(btn);
    btn.classList.remove('is-adding');
    btn.classList.add('is-added');
    btn.removeAttribute('aria-busy');
    if (l) l.textContent = t('added', ADDED);
  }

  function toReset(btn) {
    var l = labelOf(btn);
    var orig = btn.getAttribute('data-pogi-orig');
    clearFeedbackTimer(btn);
    disconnectLabelObserver(btn);
    btn.classList.remove('is-adding');
    btn.classList.remove('is-added');
    btn.removeAttribute('aria-busy');
    if (l && typeof orig === 'string') l.textContent = orig;
    btn.removeAttribute('data-pogi-orig');
    delete btn.__pogiAddOwner;
  }

  // Tap/click on any add-to-cart CTA -> show "Adding…". Bubbling listener, NO
  // preventDefault/stopPropagation, so the native form submit proceeds untouched.
  document.addEventListener('click', function (e) {
    var node = e.target;
    if (node && node.nodeType === 3) node = node.parentNode; // text node -> element
    if (!node || typeof node.closest !== 'function') return;
    var btn = node.closest('[data-pogi-atc]');
    if (!btn) return;
    // Product Form owns this lock for the exact authoritative mutation window.
    // A buy-box rerender can transiently clear `disabled`; the lock still stops
    // a deduplicated tap from creating an orphan feedback generation.
    if (btn.getAttribute('data-pogi-add-lock') === '1') return;
    if (btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;
    if (/sold\s*out/i.test(btn.textContent || '')) return; // unavailable
    var owner = ownerForButton(btn);
    if (!owner) return;
    var generation = toAdding(btn, owner);
    // Safety net: if the add never reports back (network/validation error), revert.
    btn.__pogiFeedbackTimer = window.setTimeout(function () {
      if (
        btn.__pogiFeedbackGeneration === generation
        && btn.classList.contains('is-adding')
      ) toReset(btn);
    }, FALLBACK_MS);
  });

  // Add reported back (event bubbles to document) -> resolve any in-flight CTAs.
  // product-form.js resolves with { didError: true } on FAILED/partial adds
  // too (sold-out, 422, exceed-max), so only show "Added ✓" on a genuine success;
  // otherwise just revert — never tell the customer it added when it didn't.
  function captureAddingButtons(owner) {
    var adding = document.querySelectorAll('[data-pogi-atc].is-adding');
    var tickets = [];
    for (var i = 0; i < adding.length; i++) {
      if (adding[i].__pogiAddOwner !== owner) continue;
      tickets.push({
        btn: adding[i],
        generation: adding[i].__pogiFeedbackGeneration,
      });
    }
    return tickets;
  }

  function armEventFallback(ticket) {
    clearFeedbackTimer(ticket.btn);
    ticket.btn.__pogiFeedbackTimer = window.setTimeout(function () {
      if (
        ticket.btn.__pogiFeedbackGeneration === ticket.generation
        && ticket.btn.classList.contains('is-adding')
      ) toReset(ticket.btn);
    }, EVENT_FALLBACK_MS);
  }

  function resolveAddingButtons(failed, tickets) {
    for (var i = 0; i < tickets.length; i++) {
      (function (ticket) {
        var btn = ticket.btn;
        if (
          btn.__pogiFeedbackGeneration !== ticket.generation
          || !btn.classList.contains('is-adding')
        ) return;
        if (failed) {
          toReset(btn);
          return;
        }
        toAdded(btn);
        btn.__pogiFeedbackTimer = window.setTimeout(function () {
          if (btn.__pogiFeedbackGeneration === ticket.generation) toReset(btn);
        }, ADDED_MS);
      })(tickets[i]);
    }
  }

  document.addEventListener(CART_LINES_UPDATE, function (e) {
    if (!e || e.action !== 'add' || !e.promise) return;
    var owner = ownerForNode(e.target);
    if (!owner) return;
    // Capture the visual operation synchronously with this Shopify event.
    // A newer click on the same form receives a newer generation, so neither
    // this event nor its slower drawer-render promise can settle that click.
    var tickets = captureAddingButtons(owner);
    if (tickets.length === 0) return;
    // The Shopify event now owns a bounded authoritative lifecycle. Replace
    // the short "no event arrived" safety net with a deadline long enough for
    // preflight, add/reconciliation, and the drawer morph to complete.
    for (var i = 0; i < tickets.length; i++) armEventFallback(tickets[i]);
    e.promise.then(function (result) {
      var failed = !!(result && result.detail && result.detail.didError);
      if (failed) {
        resolveAddingButtons(true, tickets);
        return;
      }

      // Shopify's cart promise confirms the line, but Horizon can still be
      // morphing the authoritative drawer section. Starting the short
      // "Added" timer before that morph finishes can make the CTA revert to
      // its idle label while the drawer is still opening. Keep "Adding…"
      // until the existing drawer render barrier settles, then start the
      // success state. Full-cart and non-drawer adds have no such barrier and
      // retain their existing behavior.
      var rendered = e.pogiSectionRenderPromise;
      if (rendered && typeof rendered.then === 'function') {
        rendered.then(function () {
          resolveAddingButtons(false, tickets);
        }, function () {
          // The cart line is already authoritative even if presentation
          // recovery rejects, so "Added" remains truthful and avoids a stuck
          // busy state while the drawer owner performs its fallback.
          resolveAddingButtons(false, tickets);
        });
        return;
      }

      resolveAddingButtons(false, tickets);
    }).catch(function () {
      resolveAddingButtons(true, tickets);
    });
  });

  // Transport/parse failures and failed variant-section requests do not have a
  // successful cart update to resolve the visual state. Reset immediately so
  // the shopper receives the form's error/retry affordance without a 6s dead wait.
  function resetAddingButtons(owner) {
    var adding = document.querySelectorAll('[data-pogi-atc].is-adding');
    for (var i = 0; i < adding.length; i++) {
      if (!owner || adding[i].__pogiAddOwner === owner) toReset(adding[i]);
    }
  }

  document.addEventListener(CART_ERROR, function (event) {
    var owner = ownerForNode(event.target);
    if (owner) resetAddingButtons(owner);
  });
  document.addEventListener('shopify:product:select', function (event) {
    if (event.promise && typeof event.promise.catch === 'function') {
      var owner = ownerForNode(event.target);
      event.promise.catch(function () {
        if (owner) resetAddingButtons(owner);
      });
    }
  });

  // Shared restore for navigate-away buttons (only used if nav/reload doesn't happen).
  function restoreBtn(btn) {
    if (!btn || btn.getAttribute('data-pogi-busy') !== '1') return;
    var o = btn.getAttribute('data-pogi-orig');
    if (typeof o === 'string') btn.innerHTML = o;
    btn.classList.remove('is-checkingout');
    btn.classList.remove('is-subscribing');
    btn.removeAttribute('aria-busy');
    btn.removeAttribute('data-pogi-busy');
    btn.removeAttribute('data-pogi-orig');
  }

  // --- Checkout buttons (cart page + drawer): they navigate away, so show a busy
  // "Checking out…" state on click + guard double-submit. NO preventDefault — the
  // native form submit (or pogi-cart #onSubmit /discount/ redirect) proceeds untouched.
  document.addEventListener('click', function (e) {
    var node = e.target;
    if (node && node.nodeType === 3) node = node.parentNode;
    if (!node || typeof node.closest !== 'function') return;
    var btn = node.closest('[data-pogi-checkout], .pogi-cartfoot__checkout');
    if (!btn || btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;
    if (btn.getAttribute('data-pogi-busy') === '1') return;
    btn.setAttribute('data-pogi-busy', '1');
    btn.setAttribute('aria-busy', 'true');
    btn.classList.add('is-checkingout');
    if (typeof btn.getAttribute('data-pogi-orig') !== 'string') btn.setAttribute('data-pogi-orig', btn.innerHTML);
    btn.innerHTML = t('checking_out', 'Checking out…');
    if (window.pogiAnnounce) window.pogiAnnounce(t('sr_checking_out', 'Checking out')); // SR cue (pogi-qol.js)
    window.setTimeout(function () { restoreBtn(btn); }, FALLBACK_MS); // rare no-nav fallback
  });

  // --- Email signup: native customer form -> full page reload. Show "Subscribing…"
  // on a valid submit (invalid required inputs never dispatch submit). Reload resets it.
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.classList || !form.classList.contains('email-signup__form')) return;
    var btn = form.querySelector('.email-signup__button');
    if (!btn || btn.getAttribute('data-pogi-busy') === '1') return;
    btn.setAttribute('data-pogi-busy', '1');
    btn.setAttribute('aria-busy', 'true');
    btn.classList.add('is-subscribing');
    if (window.pogiAnnounce) window.pogiAnnounce(t('sr_subscribing', 'Subscribing')); // SR cue, both variants (pogi-qol.js)
    // Only swap the label on the TEXT variant. The icon (arrow) variant is a fixed
    // round button with aria-label="Sign up" — swapping in "Subscribing…" would
    // overflow it AND leave a stale accessible name, so keep the icon and let
    // aria-busy + the CSS cue convey the state.
    if (!btn.querySelector('svg')) {
      if (typeof btn.getAttribute('data-pogi-orig') !== 'string') btn.setAttribute('data-pogi-orig', btn.innerHTML);
      btn.innerHTML = t('subscribing', 'Subscribing…');
    }
    window.setTimeout(function () { restoreBtn(btn); }, FALLBACK_MS);
  }, true);

  // bfcache: a page restored from back/forward cache can carry a button frozen mid
  // "Checking out…"/"Subscribing…"/"Adding…". Reset on EVERY pageshow (harmless on a
  // fresh load — nothing is busy). Critical: otherwise the data-pogi-busy guard would
  // block re-clicking Checkout, leaving a dead checkout button after a Back navigation.
  window.addEventListener('pageshow', function () {
    var busy = document.querySelectorAll('[data-pogi-busy="1"]');
    for (var i = 0; i < busy.length; i++) restoreBtn(busy[i]);
    var adding = document.querySelectorAll('[data-pogi-atc].is-adding');
    for (var j = 0; j < adding.length; j++) toReset(adding[j]);
  });
})();
